import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { v4 as uuid } from 'uuid';
import { Checkbox } from '../../../components/Checkbox';
import { Collapsible } from '../../../components/Collapsible';
import { AdvancedOptionsButton } from '../../../components/automation/AutomationStepCard/presentation';
import { styleSheet } from '../../../components/automation/AutomationStepCard/styles';
import { AutomationTagAutocomplete } from '../../../components/automation/AutomationTriggerCard/presentation';
import { ContactFilterOption } from '../../../components/automation/AutomationStepCard/components/ContactFilterOption';
import { StepSendFromOption } from '../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { IConnectionTypeComponentProps } from '../../templates/EditCampaign/context';
interface HtmlNewsletterStepAdvancedOptionsProps extends IConnectionTypeComponentProps {
	impersonationContext?: Api.IImpersonationContext;
	/**
	 * The automation step for context (passed to StepSendFromOption for send-from config).
	 */
	step: Api.IHtmlNewsletterAutomationStep | null;
	/**
	 * Current tag to add on link click.
	 */
	tagToAddOnClick: string;
	/**
	 * Whether the tag checkbox is checked.
	 */
	showEmailLinkClickTags: boolean;
	/**
	 * Current contact filter.
	 */
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	/**
	 * Whether advanced options are expanded.
	 */
	advancedOptionsToggled: boolean;
	/**
	 * Whether the form can be edited.
	 */
	canEdit: boolean;
	/**
	 * Whether data is being saved.
	 */
	isBusy: boolean;
	/**
	 * Unique key for components.
	 */
	baseKey: string;
	/**
	 * Callback when advanced options toggle changes.
	 */
	onToggleAdvancedOptions: (toggled: boolean) => void;
	/**
	 * Callback when tag on click checkbox changes.
	 */
	onShowEmailLinkClickTagsChange: (show: boolean) => void;
	/**
	 * Callback when tag value changes.
	 */
	onTagToAddOnClickChange: (tag: string) => void;
	/**
	 * Callback when contact filter changes.
	 */
	onContactFilterChange: (filter: Api.IContactsFilterRequest | null) => void;
	/**
	 * Callback when send from options change.
	 */
	onSendFromChange: (sendFrom: Api.ISendFromOptions, sendToSender: boolean) => void;
	/**
	 * Optional additional conditional options content.
	 */
	extraOptions?: React.ReactNode;
}

/**
 * Advanced options component for HTML newsletter step.
 * Includes tag on link click, send from options, and contact filter.
 */
export function HtmlNewsletterStepAdvancedOptions({
	step,
	tagToAddOnClick,
	showEmailLinkClickTags,
	skipStepContactFilter,
	advancedOptionsToggled,
	canEdit,
	isBusy,
	baseKey,
	onToggleAdvancedOptions,
	onShowEmailLinkClickTagsChange,
	onTagToAddOnClickChange,
	onContactFilterChange,
	onSendFromChange,
	extraOptions,
	impersonationContext,
	connectionTypes,
}: HtmlNewsletterStepAdvancedOptionsProps) {
	const isDisabled = !canEdit || isBusy;

	const handleAddTagToLinkClickCheckChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = e.target.checked;
		onShowEmailLinkClickTagsChange(checked);
		if (!checked) {
			onTagToAddOnClickChange('');
		}
	};

	const handleTagForLinkClickChanged = (tag: string) => {
		onTagToAddOnClickChange(tag);
	};

	return (
		<>
			<AdvancedOptionsButton
				isCollapsed={!advancedOptionsToggled}
				onClick={() => onToggleAdvancedOptions(!advancedOptionsToggled)}
			/>
			<Collapsible isCollapsed={!advancedOptionsToggled}>
				<div className={css(styleSheet.advancedOptionsContent)}>
					<Checkbox
						className={css(styleSheet.addTagOnLinkClickCheckbox)}
						id={baseKey ? `automation-add-tag-on-link-click-checkbox-${baseKey}` : uuid()}
						checked={showEmailLinkClickTags}
						type='large'
						onChange={handleAddTagToLinkClickCheckChanged}
						disabled={isDisabled}
					>
						<div>
							<div className={css(styleSheet.checkboxText)}>
								Add a tag if the recipient clicks (any) link in the email
							</div>
						</div>
					</Checkbox>
					{showEmailLinkClickTags ? (
						<AutomationTagAutocomplete
							canEdit={!isDisabled}
							className={css(styleSheet.tagAutomcomplete)}
							impersonationContext={impersonationContext}
							limit={5}
							onTagSelected={handleTagForLinkClickChanged}
							initialTag={tagToAddOnClick}
						/>
					) : null}
					{extraOptions}
					{step && (
						<StepSendFromOption
							showInternalSendFromOption
							step={step}
							onSave={onSendFromChange}
							connectionTypes={connectionTypes}
							impersonationContext={impersonationContext}
							canEdit={canEdit}
						/>
					)}
					<ContactFilterOption
						canEdit={canEdit && !isBusy}
						skipStepContactFilter={skipStepContactFilter}
						stepUuid={baseKey}
						stepIsBusy={isBusy}
						onFilterChange={onContactFilterChange}
					/>
				</div>
			</Collapsible>
		</>
	);
}
